<template>
  <default-textual-section
      v-if="$store.state.displayType === 'textual'"
      :menu="menu"
      :section="section"
      :menu-item-component="menuItemComponent"
      :section-menu-type="
      menu?.menu_type.mt_title.length
        ? menu.menu_type.mt_title.toUpperCase().replace('\'', '’')
        : ''"
      class="menu--section-new_years"
  ></default-textual-section>
  <default-visual-section
      v-else
      :menu="menu"
      :section="section"
      :menu-item-component="menuItemComponent"
      :section-menu-type="
      menu?.menu_type.mt_title.length
        ? menu.menu_type.mt_title.toUpperCase().replace('\'', '’')
        : ''"
      class="menu--section-new_years"
  ></default-visual-section>
</template>

<script lang="ts">
/**
 * Custom component for New Years default menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 0.0.1
 * @copyright  Copyright (C) 2024-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2024-12-09 (1.0.0) (jhoward)
 *    - Copy from Thanksgiving_defaultSection.vue component to NewYears_defaultSection.vue component.
 *
 *  2025-02-10 (1.1.0) (jhoward)
 *    - Add <menu-schedules> to the <NewYears_defaultSection> component if in admin view.
 *
 *  2025-04-08 (2.0.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <NewYears_defaultSection>.
 *
 *  2025-12-30 (2.0.1) (jhoward)
 *    - Adjust visual styles in <NewYears_defaultSection>.
 * @changelog
 *
 * 2026-07-20 (0.0.1) (jhoward)
 *   - Update App\Library\Js\Components\Menu\Section\NewYears_defaultSection.vue to adjust padding, highlight, and layout styles for New Year's section.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import DefaultVisualSection from "./visual/defaultVisualSection.vue";
import DefaultTextualSection from "./textual/defaultTextualSection.vue";

export default defineComponent({
  components: {
    DefaultTextualSection,
    DefaultVisualSection,
  },
  name: "NewYears_defaultSection",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true,
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true,
    },
    menuItemComponent: {
      type: String,
      default: "new-years-food-item",
      validate: (value: string) =>
          "" === value || ALLOWED_ITEM_COMPONENTS.indexOf(value),
    },
  },
  mounted() {
    this.section.ms_title = this.menu.page.page_h1;
    this.section.ms_subtitle = this.sectionSubtitle();
  },
  methods: {
    sectionSubtitle(): string {
      if (this.section.ms_title.length) {
        if ("new year's eve" === this.section.ms_title.toLowerCase()) {
          return this.section.ms_title.toUpperCase() + " DINING";
        }

        return this.section.ms_subtitle;
      }

      return (this.menu?.page?.page_h1 || "").toUpperCase();
    },
  },
});
</script>

<style>
html .search--container + .visual_menu--page {
  padding-top: 0;
}
</style>

<style scoped>
.menu--section-new_years {
  font-size: 1.3em;
  color: #231f20;
  padding: 6em 7rem 10rem;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
}

.menu_switcher--container + .visual_menu--page .menu--section-new_years:first-child {
  padding-top: 10rem;
}

.menu--section-new_years::before {
  content: "";
  position: absolute;
  z-index: -1;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/library/images/menu/background-new-years.jpg");
  background-size: 100% 100%;
  background-position: top center;
}

.menu--section-new_years:deep(.menu--highlight) {
  /*color: #9a7326;*/
  font-weight: bold;
}

.menu--section-new_years:deep(.visual_menu--page_section-bottom .menu--highlight) {
  color: #9a7326;
}

.menu--section-new_years:deep(.menu--item_description h4) {
  color: #000;
  font-weight: bold;
}

.menu--section-new_years:deep(.menu--item_title-new_years) {
  font-size: 1.2em;
  font-weight: bold;
  letter-spacing: 0.1em;
}

.menu--section-new_years:deep(h2) {
  padding-left: 0;
}

.menu--section-new_years:deep(h2 img) {
  width: 83%;
}

.menu--section-new_years:deep(.menu--section_title) {
  color: #000;
  font-weight: 300;
  font-size: 2em;
  line-height: 0.85em;
  position: relative;
  margin-bottom: 0.45em;
  padding: 0 0.5em 0.45em;
  overflow: initial;
  max-width: initial;
}

.menu--section-new_years:deep(.menu--section_menu_type::before),
.menu--section-new_years:deep(.visual_menu--page_section.visual_menu--page_section-top::after) {
  content: "........................................................";
  position: absolute;
  bottom: -5px;
  left: 10%;
  width: 80%;
  height: 33px;
  text-align: center;
  font-size: 33px;
  letter-spacing: 3px;
  color: #d1d2d4;
  line-height: 33px;
  overflow: hidden;
}

.menu--section-new_years:deep(.menu--section_menu_type::before) {
  top: -45px;
  bottom: auto;
}

.menu--section-new_years:deep(
    .visual_menu--page_section.visual_menu--page_section-top
  )::after {
  bottom: 12px;
}

.menu--section-new_years:deep(.menu--section_menu_type) {
  position: relative;
  text-align: center;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
  color: #9a7326;
  font-size: 1.5em;
  line-height: 1em;
  font-weight: lighter;
  letter-spacing: 0.1em;
  padding: 0 1.1em;
}

.menu--section-new_years:deep(.menu--section_subtitle) {
  text-align: center;
  font-family: itc-avant-garde-gothic-pro, sans-serif;
  color: #000;
  font-size: 1em;
  line-height: 1.4em;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
}

.menu--section-new_years:deep(.visual_menu--page_section.visual_menu--page_section-top) {
  position: relative;
  font-size: 0.8em;
  font-weight: lighter;
  padding-bottom: 2.1em;
}

.menu--section-new_years:deep(.visual_menu--page_section.visual_menu--page_section-top p) {
  margin-top: 0;
  margin-bottom: 0;
}

.menu--section-new_years:deep(.visual_menu--page_section-bottom .visual_menu--page_section-bottom) {
  padding-top: 1.25em;
  position: relative;
  font-size: 0.9em;
  font-weight: lighter;
  padding-left: 1rem;
  padding-right: 1rem;
  /*left: -3em;
  width: calc(100% + 6em);*/
}

.menu--section-new_years:deep(.menu--item_group) {
  padding-left: 5rem;
  padding-right: 5rem;
}
</style>